import * as Sentry from "@sentry/react";
import { Box } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";

import "./App.css";
import { MobileDetectionWrapper } from "./components";
import { PortfolioLayout, PortfolioView } from "./components/Portfolio";
import ResearchWorkflowView from "./components/ResearchWorkflow/ResearchWorkflowView";
import VersionSkewToast from "./components/VersionSkewToast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import AcceptInvite from "./pages/AcceptInvite";
import AccountDeactivated from "./pages/AccountDeactivated";
import AccountActivation from "./pages/AccountActivation";
import EntityDetailPage from "./pages/EntityDetailPage";
import ForgotPassword from "./pages/ForgotPassword";
import GraphView from "./pages/GraphView";
import ListView from "./pages/ListView";
import Login from "./pages/Login";
import MapView from "./pages/MapView";
import Portfolios from "./pages/Portfolios";
import RelationDetailPage from "./pages/RelationDetailPage";
import ResetPassword from "./pages/ResetPassword";
import Settings from "./pages/Settings";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import CapabilityMapView from "./pages/CapabilityMapView";
import DocumentsPage from "./pages/DocumentsPage";
import AccountIntelligencePage from "./pages/AccountIntelligencePage";
import ExplorePage from "./pages/ExplorePage";
import ValueChainPage from "./pages/ValueChainPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ComparePage from "./pages/ComparePage";
import { darkTheme } from "./theme";
import { ExportStageProvider } from "./utils/export/ExportStage";

// Plain `Routes`, not `Sentry.withSentryReactRouterV6Routing(Routes)`: that
// wrapper only exists to name transactions for `browserTracingIntegration`,
// which is no longer registered (#1434). `Sentry.ErrorBoundary` below is
// unaffected — it reports errors, not spans.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// The TanStack Query devtools and their floating button: off unless the
// dev server is started with REACT_APP_QUERY_DEVTOOLS=true
const QUERY_DEVTOOLS = process.env.REACT_APP_QUERY_DEVTOOLS === "true";

// Feature-to-route mapping (order = priority for default redirect)
const FEATURE_ROUTES: { feature: string | string[]; path: string }[] = [
  { feature: ["research", "research_viewer"], path: "/research" },
  { feature: "products", path: "/explore" },
  { feature: "products", path: "/value-chain" },
  { feature: "products", path: "/analytics" },
  { feature: "products", path: "/compare" },
  { feature: "search", path: "/search" },
  { feature: "map", path: "/map" },
  { feature: "graph", path: "/graph" },
  { feature: "portfolios", path: "/portfolios" },
  { feature: "capability_map", path: "/capability-map" },
  { feature: "account_intelligence", path: "/account-intelligence" },
];

// The screen while the session is checked, mostly a split second on load:
// empty, in the page's colour, so that nothing flashes before the page
// shows its own loading state
const AuthPending: React.FC = () => (
  <Box sx={{ height: "100vh", backgroundColor: "background.default" }} />
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <AuthPending />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has pending activation, deactivated, or expired access
  if (user.account_status === "DEACTIVATED") {
    return <Navigate to="/deactivated" replace />;
  }

  if (user.account_status === "PENDING_ACTIVATION") {
    return <Navigate to="/activate" replace />;
  }

  // Check for expired access (trial or full subscription)
  if (user.is_access_expired || user.is_trial_expired) {
    return <Navigate to="/activate" replace />;
  }

  return <MobileDetectionWrapper>{children}</MobileDetectionWrapper>;
};

// Feature-gated route: redirects to first available feature if user lacks access
const FeatureRoute: React.FC<{
  feature: string | string[];
  children: React.ReactNode;
}> = ({ feature, children }) => {
  const { hasFeature, loading } = useAuth();

  if (loading) {
    return <AuthPending />;
  }

  if (!hasFeature(feature)) {
    const fallback = FEATURE_ROUTES.find((r) => hasFeature(r.feature));
    return <Navigate to={fallback?.path ?? "/"} replace />;
  }

  return <ProtectedRoute>{children}</ProtectedRoute>;
};

// Default redirect: go to first available feature
const DefaultRedirect: React.FC = () => {
  const { user, loading, hasFeature } = useAuth();

  if (loading) {
    return <AuthPending />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.account_status === "DEACTIVATED") {
    return <Navigate to="/deactivated" replace />;
  }

  const first = FEATURE_ROUTES.find((r) => hasFeature(r.feature));
  return <Navigate to={first?.path ?? "/search"} replace />;
};

// App Routes Component (needs to be inside AuthProvider to use useAuth)
const AppRoutes: React.FC = () => {
  return (
    <Router>
      {/* Inside the router, so that an exported subtree can link and navigate */}
      <ExportStageProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/activate" element={<AccountActivation />} />
        <Route path="/deactivated" element={<AccountDeactivated />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<DefaultRedirect />} />
        <Route
          path="/map"
          element={
            <FeatureRoute feature="map">
              <MapView />
            </FeatureRoute>
          }
        />
        <Route
          path="/graph"
          element={
            <FeatureRoute feature="graph">
              <GraphView />
            </FeatureRoute>
          }
        />
        <Route
          path="/search"
          element={
            <FeatureRoute feature="search">
              <ListView />
            </FeatureRoute>
          }
        />
        <Route
          path="/research"
          element={
            <FeatureRoute feature={["research", "research_viewer"]}>
              <ResearchWorkflowView />
            </FeatureRoute>
          }
        />
        <Route
          path="/research/:projectId"
          element={
            <FeatureRoute feature={["research", "research_viewer"]}>
              <ResearchWorkflowView />
            </FeatureRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <FeatureRoute feature="research">
              <DocumentsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/portfolios"
          element={
            <FeatureRoute feature="portfolios">
              <Portfolios />
            </FeatureRoute>
          }
        />
        <Route
          path="/portfolio"
          element={
            <FeatureRoute feature="portfolios">
              <PortfolioLayout />
            </FeatureRoute>
          }
        >
          <Route index element={<PortfolioView />} />
        </Route>
        <Route
          path="/entity/:entityId"
          element={
            <ProtectedRoute>
              <EntityDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/relation/:relationshipId"
          element={
            <ProtectedRoute>
              <RelationDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/capability-map"
          element={
            <FeatureRoute feature="capability_map">
              <CapabilityMapView />
            </FeatureRoute>
          }
        />
        <Route
          path="/account-intelligence"
          element={
            <FeatureRoute feature="account_intelligence">
              <AccountIntelligencePage />
            </FeatureRoute>
          }
        />
        <Route
          path="/explore"
          element={
            <FeatureRoute feature="products">
              <ExplorePage />
            </FeatureRoute>
          }
        />
        <Route
          path="/value-chain"
          element={
            <FeatureRoute feature="products">
              <ValueChainPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <FeatureRoute feature="products">
              <AnalyticsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/compare"
          element={
            <FeatureRoute feature="products">
              <ComparePage />
            </FeatureRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
      </Routes>
      </ExportStageProvider>
    </Router>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        {/* <BetaBanner /> */}
        <VersionSkewToast />
        <Sentry.ErrorBoundary fallback={<p>An error has occurred.</p>}>
          <AuthProvider>
            <NotificationProvider>
              <AppRoutes />
            </NotificationProvider>
          </AuthProvider>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
      {QUERY_DEVTOOLS && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
